import type { RegistrationDraft } from '@/types/registration';
import type { RegistrationStepKey } from '@/stores/registration';

export const REGISTRATION_STORAGE_KEY = 'lead_registration_draft';
export const FEATURE_SETUP_WARNING_STORAGE_KEY = 'lead_registration_feature_setup_warning';

/** bump when the draft shape changes, so stale state is discarded rather than half-restored */
export const REGISTRATION_STORAGE_VERSION = 4;

interface PersistedRegistration {
  v: number;
  step: RegistrationStepKey;
  draft: RegistrationDraft;
  /** whether the email was already verified; decides how far a position can be restored after a reload (e.g. the language switcher's), and a session still outranks it */
  emailVerified: boolean;
  /** whether the mobile was already confirmed, so a language-switch reload doesn't re-prompt */
  mobileVerified: boolean;
}

/** fields that must never reach storage: the password is sent once, and verification codes are single-use */
const NEVER_PERSIST = ['password', 'passwordConfirmation', 'emailCode', 'smsCode', 'code'];

function stripSecrets(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripSecrets);

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([key]) => !NEVER_PERSIST.includes(key))
        .map(([key, nested]) => [key, stripSecrets(nested)])
    );
  }

  return value;
}

/** session storage survives a reload but dies with the tab, so abandoning the wizard starts over */
export function writePersistedRegistration(
  draft: RegistrationDraft,
  step: RegistrationStepKey,
  emailVerified = false,
  mobileVerified = false
) {
  try {
    const payload: PersistedRegistration = {
      v: REGISTRATION_STORAGE_VERSION,
      step,
      draft: stripSecrets(draft) as RegistrationDraft,
      emailVerified,
      mobileVerified,
    };

    window.sessionStorage.setItem(REGISTRATION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // storage can be full or blocked; losing the safety net is not worth breaking the wizard over
  }
}

export function readPersistedRegistration(): PersistedRegistration | null {
  const raw = window.sessionStorage.getItem(REGISTRATION_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PersistedRegistration>;

    if (parsed.v !== REGISTRATION_STORAGE_VERSION) return null;
    if (!parsed.draft || !parsed.step) return null;

    return {
      ...parsed,
      emailVerified: parsed.emailVerified === true,
      mobileVerified: parsed.mobileVerified === true,
    } as PersistedRegistration;
  } catch {
    return null;
  }
}

export function clearPersistedRegistration() {
  window.sessionStorage.removeItem(REGISTRATION_STORAGE_KEY);
}

/**
 * Own key rather than folded into the draft above: the draft is wiped the moment the wizard
 * reaches the completion step, but that's exactly where this warning first needs to be read.
 */
export function writeFeatureSetupWarning(hasWarning: boolean) {
  try {
    if (hasWarning) window.sessionStorage.setItem(FEATURE_SETUP_WARNING_STORAGE_KEY, '1');
    else window.sessionStorage.removeItem(FEATURE_SETUP_WARNING_STORAGE_KEY);
  } catch {
    // storage can be full or blocked; losing the safety net is not worth breaking the wizard over
  }
}

export function readFeatureSetupWarning(): boolean {
  try {
    return window.sessionStorage.getItem(FEATURE_SETUP_WARNING_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

export function clearFeatureSetupWarning() {
  window.sessionStorage.removeItem(FEATURE_SETUP_WARNING_STORAGE_KEY);
}
